import {Component, DestroyRef, Inject, OnInit, inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {HttpEventType} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {UploadingSteps} from '../../uploading-steps';
import {FileUploadService} from '../../../service/fileUpload.service';
import {TuningsService} from '../../../service/Tunings.service';
import {IdentifyStockFileModel} from '../../../models/IdentifyStockFile-model';

interface MatchTuneDialogData {
  initialFile: File | null;
}

@Component({
  selector: 'app-match-tune-dialog',
  templateUrl: './match-tune-dialog.component.html',
  styleUrls: ['./match-tune-dialog.component.css']
})
export class MatchTuneDialogComponent implements OnInit {

  private readonly fileUploadService = inject(FileUploadService);
  private readonly tuningService = inject(TuningsService);
  private readonly destroyRef = inject(DestroyRef);

  public currentStep: UploadingSteps = UploadingSteps.UPLOAD_FILE;
  public progressWidth: number = 0;
  public stockFileDTO: IdentifyStockFileModel | null = null;
  public error: string | null = null;
  public fileName: string = '';
  /** Shown in analyzing state */
  public fileSizeLabel: string = '';

  protected readonly UploadingSteps = UploadingSteps;

  /** Circumference of progress ring (r=40, stroke 6) */
  readonly progressRingLen = 2 * Math.PI * 40;

  constructor(
    public dialogRef: MatDialogRef<MatchTuneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MatchTuneDialogData
  ) {
  }

  ngOnInit(): void {
    if (this.data?.initialFile) {
      this.beginUpload(this.data.initialFile);
    }
  }

  /** Checklist rows driven by upload progress (FTW-0). */
  analyzingStep1Done(): boolean {
    return this.progressWidth >= 32;
  }

  analyzingStep2Done(): boolean {
    return this.progressWidth >= 58;
  }

  analyzingStep3Active(): boolean {
    return this.progressWidth >= 58 && this.progressWidth < 98;
  }

  analyzingStep3Done(): boolean {
    return this.progressWidth >= 98;
  }

  analyzingStep4Active(): boolean {
    return this.progressWidth >= 98;
  }

  beginUpload(file: File): void {
    this.resetState();
    this.fileName = file.name;
    this.fileSizeLabel = this.formatFileSize(file.size);
    this.currentStep = UploadingSteps.STOCK_FILE_OPTIONS;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];

      this.fileUploadService.uploadFileToIdentify(base64)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            if (response.type === HttpEventType.UploadProgress) {
              this.progressWidth = Math.round((response.loaded / (response.total || 1)) * 100);
            } else if (response.type === HttpEventType.Response) {
              if (response.status === 200) {
                this.stockFileDTO = response.body as IdentifyStockFileModel;
              }
            }
          },
          error: (err) => {
            if (err?.status === 404) {
              this.error = 'Sorry, file not found in our database';
            } else {
              this.error = 'Something went wrong while analyzing the file';
            }
          }
        });
    };
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  onRequestSent(sent: boolean): void {
    if (sent) {
      this.dialogRef.close('SENT');
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private resetState(): void {
    this.tuningService.selectedOptions = [];
    this.tuningService.options.set([]);
    this.progressWidth = 0;
    this.stockFileDTO = null;
    this.error = null;
    this.fileSizeLabel = '';
  }
}
